import { HttpClient } from "@angular/common/http";
import { Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import { SideBarItemInput } from "@models/ISidebar";
import { IUserSession } from "@models/IUserModel";
import { KeysEstaticas } from "@shared/constantes/KeysStaticas";
import { environment } from "environments/environment";
import { NGXLogger } from "ngx-logger";
import { catchError, lastValueFrom, Observable, of, shareReplay, throwError } from "rxjs";
import { UserApiService } from "./user-api.service";

export interface IAuthUserSessionCheck {
    idOperador: string;
    usuario: string;
    perfil: string;
    idCentroTrabajo?: string | number;
    idioma?: string;
}

export interface IPermissionsSessionCheck {
    expiration: string;
    idOperador: number;
    perfil: string;
    citaPrevia: boolean;
    reservaCitaPrevia: string;
    notificarTraspasos: boolean;
    prevision: string;
    detalleExcel: boolean;
    cuadreEntregas: boolean;
    cuadreEntregasNumeroPedido: boolean;
    gestorPedidos: boolean;
    gestorIncidencias: boolean;
    coordinadorIncidencias: boolean;
}

export interface ISessionCheckResponse {
    user: IAuthUserSessionCheck;
    permissions: IPermissionsSessionCheck;
}

@Injectable({
    providedIn: 'root',
})
export class SessionService {

    private sessionCheck$?: Observable<ISessionCheckResponse>;
    private currentUser = signal<IUserSession | null>(null);
    private currentModulos = signal<SideBarItemInput[]>([]);

    constructor(
        private http: HttpClient,
        private router: Router,
        private logger: NGXLogger,
        private userApiService: UserApiService,
    ) { }

    setUserInfo(user: IUserSession | null): void {
        this.currentUser.set(user);
    }

    getUserInfo(): IUserSession | null {
        return this.currentUser();
    }

    clearSession() {
        this.clearSessionState();
    }

    invalidateSessionCheck() {
        this.sessionCheck$ = undefined;
    }

    checkSession() {
        if (!this.sessionCheck$) {
            this.sessionCheck$ = this.http
                .get<ISessionCheckResponse>(environment.API_URL + 'session/check', { withCredentials: true })
                .pipe(
                    catchError((error) => {
                        this.sessionCheck$ = undefined;
                        return throwError(() => error);
                    }),
                    shareReplay(1)
                );
        }
        return this.sessionCheck$;
    }

    async hydrateSessionContext(): Promise<{ user: IUserSession; modulos: SideBarItemInput[] }> {
        try {
            const sessionData = await lastValueFrom(this.checkSession());

            if (!this.getUserInfo()) {
                const userFromSession = this.buildUserSessionFromCheck(sessionData);
                if (userFromSession) {
                    this.setUserInfo(userFromSession);
                }
            }

            const user = this.getUserInfo();
            if (!user) {
                throw new Error('No se pudo construir el contexto de sesion.');
            }

            const modulos = await this.getOrLoadUserModules();
            return { user, modulos };
        } catch (error) {
            this.clearSession();
            throw error;
        }
    }

    async getOrLoadUserModules(): Promise<SideBarItemInput[]> {
        const modulos = this.getModulosInMemory();

        if (modulos.length > 0) {
            return modulos;
        }

        try {
            const fetchedModulos = await lastValueFrom(this.userApiService.getModulosByPerfil());
            if (fetchedModulos.length === 0) {
                this.logger.warn('El backend devolvio una lista vacia de modulos.');
            }
            this.setModulos(fetchedModulos);
            return fetchedModulos;
        } catch (error) {
            this.handleCorruptedModules('No se pudieron recuperar los modulos desde el backend.', error);
            return [];
        }
    }

    tieneAcceso(accion: string): Observable<boolean> {
        const modulos = this.getModulosInMemory();
        if (modulos.length === 0) {
            this.logger.warn('No se encontraron modulos en memoria al comprobar permisos.');
            return of(false);
        }
        return of(modulos.some((item) => item.accion === accion));
    }

    editUserSession(updates: Partial<IUserSession>): void {
        const currentUser = this.getUserInfo();
        if (!currentUser) {
            this.logger.warn('No existe una sesion de usuario activa.');
            return;
        }

        const updatedUser: IUserSession = { ...currentUser, ...updates };
        this.setUserInfo(updatedUser);
    }

    getPerfil() {
        return this.getUserInfo()?.perfil;
    }

    getPrevision() {
        return this.getUserInfo()?.prevision;
    }

    getDetalleExcel() {
        return this.getUserInfo()?.detalleExcel;
    }

    getCoordinadorIncidencias() {
        return this.getUserInfo()?.coordinadorIncidencias;
    }

    getGestorIncidencias() {
        return this.getUserInfo()?.gestorIncidencias;
    }

    getGestorPedidos() {
        return this.getUserInfo()?.gestorPedidos;
    }

    getOperadorId() {
        return this.getUserInfo()?.idOperador;
    }

    getCuadreEntregasNumeroPedido(): boolean | undefined {
        const value = this.getUserInfo()?.cuadreEntregasNumeroPedido;
        if (value == null) return undefined;

        return String(value).toLowerCase() === 'true';
    }

    getIdioma() {
        const idiomaSesion = sessionStorage.getItem(KeysEstaticas.SESSION_IDIOMA);
        const idiomaUsuario = this.getUserInfo()?.idioma;
        return idiomaSesion ?? idiomaUsuario ?? 'es';
    }

    getUsuario() {
        return this.getUserInfo()?.username;
    }

    private buildUserSessionFromCheck(sessionData: ISessionCheckResponse | null): IUserSession | null {
        if (!sessionData) {
            return null;
        }

        const user = sessionData.user;
        const permissions = sessionData.permissions;

        if (!user || !permissions || !user.usuario) {
            return null;
        }

        const idOperador = Number(user.idOperador ?? permissions.idOperador);
        if (Number.isNaN(idOperador)) {
            return null;
        }

        return {
            idOperador,
            perfil: permissions.perfil ?? user.perfil,
            username: user.usuario,
            citaPrevia: permissions.citaPrevia,
            reservaCitaPrevia: permissions.reservaCitaPrevia,
            notificarTraspasos: permissions.notificarTraspasos,
            prevision: permissions.prevision,
            detalleExcel: permissions.detalleExcel,
            cuadreEntregas: permissions.cuadreEntregas,
            cuadreEntregasNumeroPedido: permissions.cuadreEntregasNumeroPedido,
            gestorPedidos: permissions.gestorPedidos,
            gestorIncidencias: permissions.gestorIncidencias,
            coordinadorIncidencias: permissions.coordinadorIncidencias,
            idCentroTrabajo: user.idCentroTrabajo != null ? String(user.idCentroTrabajo) : '',
            idioma: user.idioma ?? 'es',
        };
    }

    private handleCorruptedModules(message: string, error?: unknown): void {
        if (error) {
            this.logger.error(message, error);
        } else {
            this.logger.error(message);
        }

        this.clearSession();
        this.router.navigate(['/auth/login']);
    }

    clearSessionState(): void {
        this.setUserInfo(null);
        this.setModulos([]);
        this.sessionCheck$ = undefined;
    }

    private setModulos(modulos: SideBarItemInput[]): void {
        this.currentModulos.set(modulos);
    }

    private getModulosInMemory(): SideBarItemInput[] {
        return this.currentModulos();
    }




}